import { Country, Language } from 'types/Api'

type AuthErrorLink = {
  title: string
  href: string
}

type CountryConfig = {
  authErrorLinks: (undefined | AuthErrorLink)[]
  countryCode: Country
  flowMarketingUrl?: string
  hasAppSource: boolean
  languageCode: Language
  hasGoToPlatform: boolean
  loginParams: string[]
  loginUrl: string
  logoutUrl: string
  tutorialUrl?: string
}

const defaultCountryConfig: Record<Country, CountryConfig> = {
  dk: {
    authErrorLinks: [],
    countryCode: 'dk',
    flowMarketingUrl: 'https://www.karnovgroup.dk/produkt/ai/kaila-flow',
    hasAppSource: true,
    languageCode: 'da',
    hasGoToPlatform: true,
    loginParams: [],
    loginUrl: '/ai-backend/dk/login',
    logoutUrl: '/ai-backend/dk/logout',
  },
  no: {
    authErrorLinks: [],
    countryCode: 'no',
    flowMarketingUrl: undefined,
    hasAppSource: true,
    languageCode: 'no',
    hasGoToPlatform: true,
    loginParams: [],
    loginUrl: '/ai-backend/no/login',
    logoutUrl: '/ai-backend/auth/oidc_sign_out',
  },
  se: {
    authErrorLinks: [],
    countryCode: 'se',
    flowMarketingUrl: 'https://www.nj.se/juno/ai',
    hasAppSource: true,
    languageCode: 'sv',
    hasGoToPlatform: true,
    loginParams: [],
    loginUrl: '/ai-backend/se/login',
    logoutUrl: '/ai-backend/se/logout',
  },
  es: {
    authErrorLinks: [],
    countryCode: 'es',
    hasAppSource: false,
    languageCode: 'es',
    hasGoToPlatform: false,
    loginParams: ['idp', 'originPlatformId'],
    loginUrl: '/ai-backend/es/login',
    logoutUrl: '/ai-backend/es/logout',
  },
  fr: {
    authErrorLinks: [],
    countryCode: 'fr',
    flowMarketingUrl: 'https://boutique.lamy-liaisons.fr/lamylia-connect.html',
    hasAppSource: false,
    languageCode: 'fr',
    hasGoToPlatform: false,
    loginParams: [],
    loginUrl: '/ai-backend/fr/login',
    logoutUrl: '/ai-backend/fr/logout',
    tutorialUrl: 'https://app.arcade.software/share/collections/Hckib9lOIzsNGHO6iey1',
  },
} as const

type EnvironmentConfig = {
  processingConversationsRefetchInterval: number
  secureCookies: boolean
}

const defaultEnvironmentConfig: Record<'development' | 'production' | 'review' | 'staging', EnvironmentConfig> = {
  development: {
    processingConversationsRefetchInterval: 10000,
    secureCookies: false,
  },
  production: {
    processingConversationsRefetchInterval: 1000,
    secureCookies: true,
  },
  review: {
    processingConversationsRefetchInterval: 1000,
    secureCookies: true,
  },
  staging: {
    processingConversationsRefetchInterval: 1000,
    secureCookies: true,
  },
}

window.configMap = {
  development: [
    {
      ...defaultEnvironmentConfig.development,
      ...defaultCountryConfig.dk,
      domain: ['http://pro-dk.localhost'],
      flowUrl: 'https://workspace-qa-dk.karnovglobal.com/',
      secureStorageTokenUrl: `/auth/kaila-dk/secure_storage_token_refresh`,
    },
    {
      ...defaultEnvironmentConfig.development,
      ...defaultCountryConfig.no,
      domain: ['http://pro-no.localhost'],
      flowUrl: 'https://workspace-qa-no.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/auth/karnov_no',
    },
    {
      ...defaultEnvironmentConfig.development,
      ...defaultCountryConfig.se,
      domain: ['http://pro-se.localhost'],
      flowUrl: 'https://workspace-qa-se.karnovglobal.com/',
      secureStorageTokenUrl: `/auth/kaila-se/secure_storage_token_refresh`,
    },
    {
      ...defaultEnvironmentConfig.development,
      ...defaultCountryConfig.es,
      domain: ['http://pro-es.localhost'],
      flowUrl: 'https://workspace-qa-es.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/es/login',
    },
    {
      ...defaultEnvironmentConfig.development,
      ...defaultCountryConfig.fr,
      authErrorLinks: [{ title: 'Bon JWT - localhost', href: 'http://bon-jwt.localhost/llkv/sign_in?code=placeholder' }],
      domain: ['http://pro-fr.localhost'],
      flowUrl: 'https://workspace-qa-fr.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/fr/login',
    },
  ],
  production: [
    {
      ...defaultEnvironmentConfig.production,
      ...defaultCountryConfig.dk,
      domain: ['https://pro.karnovgroup.dk'],
      flowUrl: 'https://kailaflow.karnovgroup.dk/',
      secureStorageTokenUrl: `/auth/kaila-dk/secure_storage_token_refresh`,
    },
    {
      ...defaultEnvironmentConfig.production,
      ...defaultCountryConfig.no,
      domain: ['https://pro.karnovgroup.no'],
      flowUrl: 'https://workspace-qa-no.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/no/login',
    },
    {
      ...defaultEnvironmentConfig.production,
      ...defaultCountryConfig.se,
      domain: ['https://juno.nj.se'],
      flowUrl: 'https://junoflow.nj.se/',
      secureStorageTokenUrl: `/auth/kaila-se/secure_storage_token_refresh`,
    },
    {
      ...defaultEnvironmentConfig.production,
      ...defaultCountryConfig.es,
      domain: ['https://spain-ai.karnovgroup.com', 'https://allegra.aranzadilaley.es'],
      flowUrl: 'https://allegraflow.aranzadilaley.es/',
      secureStorageTokenUrl: '/ai-backend/es/login',
    },
    {
      ...defaultEnvironmentConfig.production,
      ...defaultCountryConfig.fr,
      authErrorLinks: [
        { title: 'LamyLine', href: 'https://www.lamylinenew.fr' },
        { title: 'Lamyline Notaire', href: 'https://www.lamyline-notaire.fr' },
        { title: 'Lamyline Sociales', href: 'https://www.liaisons-sociales.fr' },
        { title: 'LamyLia', href: 'https://auth.lamylia.fr' },
      ],
      domain: ['https://france-ai.karnovgroup.com', 'https://www.lamylia.fr'],
      flowUrl: 'https://www.lamylia-connect.fr/',
      secureStorageTokenUrl: '/ai-backend/fr/login',
    },
  ],
  review: [
    {
      ...defaultEnvironmentConfig.review,
      ...defaultCountryConfig.dk,
      domain: ['https://kaila-review.karnov-test.dk'],
      flowUrl: 'https://workspace-qa-dk.karnovglobal.com/',
      secureStorageTokenUrl: `/auth/kaila-dk/secure_storage_token_refresh`,
    },
    {
      ...defaultEnvironmentConfig.review,
      ...defaultCountryConfig.no,
      domain: ['https://corelia-no-review.karnov-test.com'],
      flowUrl: 'https://workspace-qa-no.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/no/login',
    },
    {
      ...defaultEnvironmentConfig.review,
      ...defaultCountryConfig.se,
      domain: ['https://kaila-review.karnov-test.se'],
      flowUrl: 'https://workspace-qa-se.karnovglobal.com/',
      secureStorageTokenUrl: `/auth/kaila-se/secure_storage_token_refresh`,
    },
    {
      ...defaultEnvironmentConfig.review,
      ...defaultCountryConfig.es,
      domain: ['https://corelia-es-review.karnov-test.com'],
      flowUrl: 'https://workspace-qa-es.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/es/login',
    },
    {
      ...defaultEnvironmentConfig.review,
      ...defaultCountryConfig.fr,
      authErrorLinks: [
        { title: 'LamyLine Staging', href: 'https://lamylinebeta-stg.externalnaw.es/' },
        { title: 'LamyLia Staging', href: 'https://auth-staging.lamylia.fr/' },
      ],
      domain: ['https://corelia-fr-review.karnov-test.com'],
      flowUrl: 'https://workspace-qa-fr.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/fr/login',
    },
  ],
  staging: [
    {
      ...defaultEnvironmentConfig.staging,
      ...defaultCountryConfig.dk,
      domain: ['https://pro.karnov-test.dk'],
      flowUrl: 'https://workspace-qa-dk.karnovglobal.com/',
      secureStorageTokenUrl: `/auth/kaila-dk/secure_storage_token_refresh`,
    },
    {
      ...defaultEnvironmentConfig.staging,
      ...defaultCountryConfig.no,
      domain: ['https://corelia-no.karnov-services.com'],
      flowUrl: 'https://workspace-qa-no.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/no/login',
    },
    {
      ...defaultEnvironmentConfig.staging,
      ...defaultCountryConfig.se,
      domain: ['https://pro.karnov-test.se'],
      flowUrl: 'https://workspace-qa-se.karnovglobal.com/',
      secureStorageTokenUrl: `/auth/kaila-se/secure_storage_token_refresh`,
    },
    {
      ...defaultEnvironmentConfig.staging,
      ...defaultCountryConfig.es,
      domain: ['https://corelia-es.karnov-services.com'],
      flowUrl: 'https://workspace-qa-es.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/es/login',
    },
    {
      ...defaultEnvironmentConfig.staging,
      ...defaultCountryConfig.fr,
      authErrorLinks: [
        { title: 'LamyLine Staging', href: 'https://lamylinebeta-stg.externalnaw.es/' },
        { title: 'LamyLine Staging (Autonome)', href: 'https://auth-staging.lamylia.fr/' },
      ],
      domain: ['https://corelia-fr.karnov-services.com'],
      flowUrl: 'https://workspace-qa-fr.karnovglobal.com/',
      secureStorageTokenUrl: '/ai-backend/fr/login',
    },
  ],
}

export const getConfiguration = (origin: string = window.origin) => {
  if (!import.meta.env.VITE_NODE_ENV) {
    throw new Error('Environment mode is not set')
  }

  const currentConfig = window.configMap[import.meta.env.VITE_NODE_ENV]

  if (!currentConfig) {
    throw new Error(
      `Environment not found in map: '${import.meta.env.VITE_NODE_ENV}'`,
    )
  }

  const configuration = currentConfig.find((config) => {
    if (config.domain.some(domain => origin.includes(domain))) {
      return config
    }
    return undefined
  })

  if (!configuration) {
    throw new Error(`Config not defined for domain: ${origin}`)
  }

  return configuration
}

export const backendApiUrl = import.meta.env.VITE_BACKEND_API_URL

/*
 * Check for local development environment
 */
export const isDevEnvironment = import.meta.env.DEV
